"use client";

import { c as _c } from "react/compiler-runtime";
import { IconLoader } from "@tabler/icons-react";
import { useEffect, useLayoutEffect, useRef } from "react";
import { trackItemViewed } from "@/lib/analytics/segment";
import { hydrateStoreFromCartItem, hydrateStoreFromSpec } from "@/lib/cart";
import { hydrateStoreFromUrlParams } from "@/lib/product/pdpParams";
import { usePdpParams } from "@/providers/PdpParamsProvider";
import { useCartItem } from "@/queries/useCartItem";
import { useProduct } from "@/queries/useProduct";
import { useSpec } from "@/queries/useSpec";
import { usePdpStore } from "@/stores/usePdpStore";
import ActionButtons from "./ActionButtons";
import Adhesive from "./Adhesive";
import AdvancedOptions from "./AdvancedOptions";
import Description from "./Description";
import Finish from "./Finish";
import Gallery from "./Gallery";
import Headline from "./Headline";
import ImageUpload from "./ImageUpload";
import Lamination from "./Lamination";
import Material from "./Material";
import MaterialWarning from "./MaterialWarning";
import { PackagingQuantity } from "./PackagingQuantity";
import Product from "./Product";
import { ProductBuilderProvider, type ResolvedProductWrapper } from "./ProductBuilderContext";
import ProductDataMismatchWarning from "./ProductDataMismatchWarning";
import Quantity from "./Quantity";
import RollFinishing from "./RollFinishing";
import Shape from "./Shape";
import Size from "./Size";
import SpecialInstructions from "./SpecialInstructions";
import StartingAtPrice from "./StartingAtPrice";
import Title from "./Title";
import TotalPrice from "./TotalPrice";
import TrustProps from "./TrustProps";
import TrustpilotProductReviews from "./TrustpilotProductReviews";
interface Props {
  resolvedWrappers: ResolvedProductWrapper[];
  parentProductTitle?: string;
  parentProductDescription?: string;
}
interface HydrateInput {
  guard: React.RefObject<boolean>;
  pdpParams: ReturnType<typeof usePdpParams>;
  cartItemData: ReturnType<typeof useCartItem>["data"];
  specData: ReturnType<typeof useSpec>["data"];
  productAdmin: ReturnType<typeof useProduct>["data"];
  resolvedWrappers: ResolvedProductWrapper[];
}
function hydrateStore({
  guard,
  pdpParams,
  cartItemData,
  specData,
  productAdmin,
  resolvedWrappers
}: HydrateInput) {
  if (guard.current) return;
  if (cartItemData) {
    guard.current = true;
    hydrateStoreFromCartItem(cartItemData);
  } else if (specData) {
    guard.current = true;
    hydrateStoreFromSpec(specData);
  } else if (!pdpParams.itemId && !pdpParams.specId && productAdmin) {
    guard.current = true;
    const validatedProduct = pdpParams.product && resolvedWrappers.some(w => w.product.sku === pdpParams.product) ? pdpParams.product : undefined;
    hydrateStoreFromUrlParams({
      ...pdpParams,
      product: validatedProduct
    }, productAdmin);
  }
}
export default function ProductBuilderClient(t0) {
  const $ = _c(44);
  const {
    resolvedWrappers,
    parentProductTitle,
    parentProductDescription
  } = t0;
  const pdpParams = usePdpParams();
  const {
    itemId,
    specId
  } = pdpParams;
  const cartItem = useCartItem(itemId);
  const spec = useSpec(specId);
  const isHydrating = Boolean(itemId) && cartItem.isPending || Boolean(specId) && spec.isPending;
  const productSku = usePdpStore(_temp) ?? pdpParams.product;
  let t1;
  if ($[0] !== productSku || $[1] !== resolvedWrappers) {
    t1 = resolvedWrappers.find(w => w.product.sku === productSku) ?? resolvedWrappers[0];
    $[0] = productSku;
    $[1] = resolvedWrappers;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const active = t1;
  const {
    data: productAdmin
  } = useProduct(active.product.sku);
  const hydrateGuard = useRef(false);
  let t2;
  let t3;
  if ($[3] !== cartItem.data || $[4] !== isHydrating || $[5] !== pdpParams || $[6] !== productAdmin || $[7] !== resolvedWrappers || $[8] !== spec.data) {
    t2 = () => {
      hydrateStore({
        guard: hydrateGuard,
        pdpParams,
        cartItemData: cartItem.data,
        specData: spec.data,
        productAdmin,
        resolvedWrappers
      });
      if (!isHydrating) {
        usePdpStore.getState().completeHydration();
      }
    };
    t3 = [pdpParams, cartItem.data, spec.data, productAdmin, resolvedWrappers, isHydrating];
    $[3] = cartItem.data;
    $[4] = isHydrating;
    $[5] = pdpParams;
    $[6] = productAdmin;
    $[7] = resolvedWrappers;
    $[8] = spec.data;
    $[9] = t2;
    $[10] = t3;
  } else {
    t2 = $[9];
    t3 = $[10];
  }
  useLayoutEffect(t2, t3);
  const trackedViewRef = useRef(false);
  let t4;
  let t5;
  if ($[11] !== productAdmin || $[12] !== specId) {
    t4 = () => {
      if (trackedViewRef.current || !productAdmin) {
        return;
      }
      trackedViewRef.current = true;
      trackItemViewed({
        product: productAdmin,
        isSpecLoaded: !!specId
      });
    };
    t5 = [productAdmin, specId];
    $[11] = productAdmin;
    $[12] = specId;
    $[13] = t4;
    $[14] = t5;
  } else {
    t4 = $[13];
    t5 = $[14];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => () => {
      hydrateGuard.current = false;
      usePdpStore.getState().resetSelections();
    };
    t7 = [];
    $[15] = t6;
    $[16] = t7;
  } else {
    t6 = $[15];
    t7 = $[16];
  }
  useLayoutEffect(t6, t7);
  let t8;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="order-1 lg:col-start-2 lg:row-start-1"><Title /><TrustpilotProductReviews /><Description /></div>;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <div className="order-2 lg:col-start-1 lg:row-start-1 lg:row-span-2"><Gallery /></div>;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== isHydrating) {
    t10 = isHydrating && <output aria-label="Loading item" className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white/70"><span className="flex items-center gap-2 text-muted-foreground text-sm"><IconLoader className="animate-spin" aria-hidden="true" size={18} />Loading item...</span></output>;
    $[19] = isHydrating;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <StartingAtPrice />;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <ProductDataMismatchWarning />;
    t13 = <Headline />;
    t14 = <Product />;
    t15 = <Material />;
    t16 = <MaterialWarning />;
    t17 = <Lamination />;
    t18 = <Shape />;
    t19 = <Size />;
    t20 = <Finish />;
    t21 = <Adhesive />;
    t22 = <Quantity />;
    t23 = <ImageUpload />;
    $[22] = t12;
    $[23] = t13;
    $[24] = t14;
    $[25] = t15;
    $[26] = t16;
    $[27] = t17;
    $[28] = t18;
    $[29] = t19;
    $[30] = t20;
    $[31] = t21;
    $[32] = t22;
    $[33] = t23;
  } else {
    t12 = $[22];
    t13 = $[23];
    t14 = $[24];
    t15 = $[25];
    t16 = $[26];
    t17 = $[27];
    t18 = $[28];
    t19 = $[29];
    t20 = $[30];
    t21 = $[31];
    t22 = $[32];
    t23 = $[33];
  }
  let t24;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <div className="flex flex-col gap-8">{t12}{t13}{t14}{t15}{t16}{t17}{t18}{t19}{t20}{t21}{t22}{t23}<AdvancedOptions><RollFinishing /><PackagingQuantity /><SpecialInstructions /></AdvancedOptions><TotalPrice /><ActionButtons /><TrustProps /></div>;
    $[34] = t24;
  } else {
    t24 = $[34];
  }
  let t25;
  if ($[35] !== t10) {
    t25 = <section className="bg-card p-6 rounded-lg shadow-sm max-w-site-content m-auto"><div className="flex flex-col lg:grid lg:grid-cols-[3fr_2fr] lg:grid-rows-[auto_1fr] lg:gap-8">{t8}{t9}<div className="order-3 lg:col-start-2 lg:row-start-2 relative">{t10}{t11}{t24}</div></div></section>;
    $[35] = t10;
    $[36] = t25;
  } else {
    t25 = $[36];
  }
  let t26;
  if ($[37] !== active.product || $[38] !== active.productWrapper || $[39] !== parentProductDescription || $[40] !== parentProductTitle || $[41] !== resolvedWrappers || $[42] !== t25) {
    t26 = <ProductBuilderProvider product={active.product} productWrapper={active.productWrapper} resolvedWrappers={resolvedWrappers} parentProductTitle={parentProductTitle} parentProductDescription={parentProductDescription}>{t25}</ProductBuilderProvider>;
    $[37] = active.product;
    $[38] = active.productWrapper;
    $[39] = parentProductDescription;
    $[40] = parentProductTitle;
    $[41] = resolvedWrappers;
    $[42] = t25;
    $[43] = t26;
  } else {
    t26 = $[43];
  }
  return t26;
}
function _temp(state) {
  return state.productSku;
}